import type { PaginationType } from '../types/Pagination.types';
import { z } from 'zod';
import { ProgressStatusName } from '../progress-statuses/progress-statuses.enum';
import { SortOrderSchema } from '../shared/schema';
import { PaginationParamsSchema } from '../types/Pagination.types';

export const REFEREES_MAX_LIMIT = 50;

export const UserReferralDtoSchema = z.object({
  code: z.string(),
  userId: z.string().uuid(),
}).describe('UserReferralDtoSchema');

export const UserRefereeRewardDtoSchema = z.object({
  // Null until the reward is granted to the referrer.
  id: z.string().uuid().nullable(),
  // Referee status that unlocks this reward.
  status: z.nativeEnum(ProgressStatusName).nullable(),
  // Bonus SC amount in cents.
  amount: z.number(),
  claimedAt: z.string().datetime().nullable(),
}).describe('UserRefereeRewardDtoSchema');

export const UserRefereeDtoSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  // Null when gamification has no player profile for the invited user yet.
  status: z.nativeEnum(ProgressStatusName).nullable(),
  level: z.number().int().min(1).nullable(),
  rewards: z.array(UserRefereeRewardDtoSchema),
  createdAt: z.string().datetime(),
}).describe('UserRefereeDtoSchema');

export const UserRefereeSummaryDtoSchema = z.object({
  totalInvitedUsers: z.number(),
  // Bonus SC granted and waiting to be claimed, in cents.
  pendingRewards: z.number(),
  // Bonus SC already claimed, in cents.
  claimedRewards: z.number(),
}).describe('UserRefereeSummaryDtoSchema');

export const AvailableRefereeRewardsCountDtoSchema = z.object({
  // Number of invited users with rewards available to claim.
  count: z.number().int().min(0),
}).describe('AvailableRefereeRewardsCountDtoSchema');

// The referees endpoint has no filters. It sorts by invite date (`createdAt`) or the last status up (`updatedAt`),
// newest invite first by default.
export const UserRefereeParamsSchema = PaginationParamsSchema().extend({
  limit: z.number().int().min(1).max(REFEREES_MAX_LIMIT).optional(),
  sort: z.object({
    createdAt: SortOrderSchema.optional(),
    updatedAt: SortOrderSchema.optional(),
  }).optional(),
});

export const RefereeRegisteredContextSchema = z.object({
  refereeUserId: z.string().uuid(),
}).describe('RefereeRegisteredContextSchema');

export const RefereeStatusUpContextSchema = z.object({
  refereeUserId: z.string().uuid(),
  previousStatusId: z.string().uuid().nullable(),
  previousStatusName: z.string().nullable(),
  currentStatusId: z.string().uuid(),
  currentStatusName: z.string().nullable(),
}).describe('RefereeStatusUpContextSchema');

export type UserReferralDto = z.infer<typeof UserReferralDtoSchema>;
export type UserRefereeRewardDto = z.infer<typeof UserRefereeRewardDtoSchema>;
export type UserRefereeDto = z.infer<typeof UserRefereeDtoSchema>;
export type UserRefereeListDto = PaginationType<UserRefereeDto>;
export type UserRefereeParams = z.infer<typeof UserRefereeParamsSchema>;
export type UserRefereeSort = NonNullable<UserRefereeParams['sort']>;
export type UserRefereeSummaryDto = z.infer<typeof UserRefereeSummaryDtoSchema>;
export type AvailableRefereeRewardsCountDto = z.infer<typeof AvailableRefereeRewardsCountDtoSchema>;
export type RefereeRegisteredContext = z.infer<typeof RefereeRegisteredContextSchema>;
export type RefereeStatusUpContext = z.infer<typeof RefereeStatusUpContextSchema>;
